import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading"); setError(null);
    try {
      await axios.post(`${API}/leads`, form);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      setStatus("error");
      setError(err?.response?.data?.detail || "No hemos podido enviar tu solicitud. Inténtalo de nuevo.");
    }
  };

  if (status === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl bg-sun text-ink p-8 text-center border-2 border-ink"
        data-testid="contact-success"
      >
        <CheckCircle2 className="w-10 h-10 mx-auto" strokeWidth={1.6} />
        <h4 className="font-serif text-3xl mt-3">¡Recibido! Te llamamos.</h4>
        <p className="mt-2 text-ink/80">En menos de 24h un gestor energético te llama con un estudio personalizado.</p>
        <button onClick={() => setStatus("idle")} data-testid="contact-success-reset"
          className="mt-5 underline font-bold hover:text-ink/60">Enviar otra solicitud</button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Nombre" testid="contact-name">
          <input required value={form.name} onChange={update("name")} placeholder="Cómo te llamas"
            data-testid="contact-name-input" className="contact-input" />
        </Field>
        <Field label="Email" testid="contact-email">
          <input required type="email" value={form.email} onChange={update("email")} placeholder="tu@email.com"
            data-testid="contact-email-input" className="contact-input" />
        </Field>
      </div>
      <Field label="Teléfono (opcional)" testid="contact-phone">
        <input value={form.phone} onChange={update("phone")} placeholder="+34 600 000 000"
          data-testid="contact-phone-input" className="contact-input" />
      </Field>
      <Field label="Cuéntanos brevemente" testid="contact-message">
        <textarea rows={4} value={form.message} onChange={update("message")}
          placeholder="Vivienda, negocio, comunidad… cualquier detalle ayuda."
          data-testid="contact-message-input" className="contact-input resize-none" />
      </Field>
      {error && <p className="text-sm text-sun" data-testid="contact-error">{error}</p>}
      <button type="submit" disabled={status === "loading"} data-testid="contact-submit"
        className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-sun text-ink font-bold hover:bg-white transition-colors disabled:opacity-50 border-2 border-sun hover:border-white">
        <Send className="w-4 h-4" />
        {status === "loading" ? "Enviando…" : "Pedir mi estudio gratuito"}
      </button>
      <style>{`
        .contact-input {
          width: 100%;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 12px;
          padding: 12px 14px;
          color: #FFFFFF;
          font-family: 'Manrope', sans-serif;
          font-size: 15px;
          outline: none;
          transition: border-color .2s, background .2s;
        }
        .contact-input::placeholder { color: rgba(255,255,255,0.40); }
        .contact-input:focus { border-color: #FFD600; background: rgba(255,214,0,0.06); }
      `}</style>
    </form>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="text-[11px] uppercase tracking-[0.2em] text-sun/80 font-bold mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
